/**
 * Algolia v5 compatibility shim.
 *
 * algoliasearch v5 removed `client.initIndex()`, but several legacy
 * libraries (autocomplete.js v0.36, instantsearch.js v4 helper) still
 * call it internally. This module provides a single polyfill that can
 * be imported wherever an Algolia client is created.
 *
 * Usage:
 *   import { polyfillInitIndex } from './algolia_compat';
 *   const client = algoliasearch(appId, apiKey);
 *   polyfillInitIndex(client);
 */

/**
 * Adds a `client.initIndex(indexName)` method to an algoliasearch v5
 * client, returning an object with `search`, `getObject`, and
 * `searchForFacetValues` methods that delegate to the v5 multi-query API.
 *
 * No-ops if `initIndex` already exists (e.g. algoliasearch v3/v4).
 */
export function polyfillInitIndex(client) {
    // Ensure the client has a _ua string (autocomplete.js v0.36 reads it)
    if (!client._ua) {
        client._ua = 'Algolia for JavaScript (3.0.0)';
    }

    if (typeof client.initIndex === 'function') {
        // Wrap existing initIndex to ensure returned objects have `as` (parent ref)
        const originalInitIndex = client.initIndex.bind(client);
        client.initIndex = function (indexName) {
            const index = originalInitIndex(indexName);
            if (!index.as) { index.as = client; }
            // Wrap search to support callback signature used by autocomplete.js
            const originalSearch = index.search.bind(index);
            index.search = function (query, params, cb) {
                const promise = originalSearch(query, params);
                if (typeof cb === 'function') {
                    promise.then(function (res) { cb(null, res); }, function (err) { cb(err); });
                }
                return promise;
            };
            return index;
        };
        return;
    }

    client.initIndex = function (indexName) {
        return {
            indexName: indexName,
            as: client,
            search: function (query, params, cb) {
                var promise = client
                    .search([{ indexName: indexName, query: query, params: params }])
                    .then(function (res) { return res.results[0]; });
                if (typeof cb === 'function') {
                    promise.then(function (res) { cb(null, res); }, function (err) { cb(err); });
                }
                return promise;
            },
            getObject: function (objectID, params) {
                return client.getObject({
                    indexName: indexName,
                    objectID: objectID,
                    attributesToRetrieve: params ? params.attributesToRetrieve : undefined
                });
            },
            searchForFacetValues: function (params) {
                return client
                    .searchForFacetValues([{
                        indexName: indexName,
                        params: typeof params === 'string' ? { facetName: params } : params
                    }])
                    .then(function (res) { return res[0]; });
            },
            findAnswers: function () {
                return Promise.reject(new Error('findAnswers is not supported in algoliasearch v5'));
            }
        };
    };
}
